"use client";

import { useState, useEffect } from "react";

type View = "portal" | "pdf" | "chat" | "tools";

interface HeaderProps {
  activeView: View;
  setActiveView: (v: View) => void;
}

export default function Header({ activeView, setActiveView }: HeaderProps) {
  const [accountUser, setAccountUser] = useState<string | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem("account_user");
    if (raw) {
      try { setAccountUser(JSON.parse(raw).username); } catch { /* ignore */ }
    }
    // Re-check when storage changes (e.g. login/logout in another tab)
    const onStorage = (e: StorageEvent) => {
      if (e.key === "account_user") {
        const val = e.newValue;
        setAccountUser(val ? JSON.parse(val).username : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
              style={{ background: "linear-gradient(135deg, #2d6a4f, #52b788)" }}>
              A
            </div>
            <div>
              <div className="font-bold text-gray-900 leading-tight">Lokalt Innovations hus i Haslev by</div>
              <div className="text-xs text-gray-500">Scenekunst · Innovation · Eksperiment</div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {[
              { id: "portal" as View, label: "🏠 Portal" },
              { id: "pdf" as View, label: "📄 Dokumenter" },
              { id: "chat" as View, label: "💬 AI Chat" },
              { id: "tools" as View, label: "🔧 Værktøjsbibliotek" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeView === item.id
                    ? "text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                style={activeView === item.id ? { backgroundColor: "#2d6a4f" } : {}}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Account + Admin links */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="/account"
              className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors"
              style={accountUser
                ? { backgroundColor: "#2d6a4f", color: "#fff", borderColor: "#2d6a4f" }
                : { color: "#4b5563", borderColor: "#d1d5db", backgroundColor: "transparent" }
              }
            >
              {accountUser ? (
                <>
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                    {accountUser.charAt(0).toUpperCase()}
                  </span>
                  <span className="font-medium">{accountUser}</span>
                </>
              ) : (
                <>👤 Min konto</>
              )}
            </a>
            <a
              href="/admin"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ⚙️ Admin
            </a>
          </div>

          {/* Mobile nav */}
          <div className="flex md:hidden gap-2">
            {[
              { id: "portal" as View, label: "🏠" },
              { id: "pdf" as View, label: "📄" },
              { id: "chat" as View, label: "💬" },
              { id: "tools" as View, label: "🔧" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                className={`p-2 rounded-lg text-lg transition-colors ${
                  activeView === item.id ? "text-white" : "text-gray-600"
                }`}
                style={activeView === item.id ? { backgroundColor: "#2d6a4f" } : {}}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
